// Conversation quality at a glance: how often conversations go wrong, how users rate them, what the model reviews
// found, per day and per organization. Every figure links to the conversations behind it.
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { int, useQueryParams } from '../../hooks/useQueryParams';
import { usd } from '../../services/format';
import { BarChart } from '../../components/charts/BarChart';
import { Alert, Card, Cell, Field, Filters, PageTitle, Row, Select, Stat, Table } from '../../components/ui';
import { OrgSelect, RankList, pct, useLabel } from './common';

const PERIODS = [7, 30, 90];

export function Insights() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { get, set, reset } = useQueryParams();
  const days = int(get('days')) ?? 30;
  const organizationId = int(get('organization_id'));
  const { data, error, loading } = useAsync(() => api.insights({ days, organization_id: organizationId }), [days, organizationId]);
  const reason = useLabel('reason');
  const misuseKind = useLabel('misuseKind');
  const tag = useLabel('tag');
  const signal = useLabel('signal');
  const outcome = useLabel('outcome');
  const category = useLabel('category');
  const dayLabel = (p: string) => {
    const d = new Date(`${p}T00:00:00`);
    return Number.isNaN(d.getTime()) ? p : new Intl.DateTimeFormat(i18n.language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short' }).format(d);
  };
  /** The conversations list with this window and organization, plus `extra` filters. */
  const listLink = (extra: Record<string, string> = {}) => {
    const params = new URLSearchParams({ days: String(days), ...(organizationId ? { organization_id: String(organizationId) } : {}), ...extra });
    return `/conversations?${params.toString()}`;
  };

  return (
    <div>
      <PageTitle title={t('quality.insightsTitle')} subtitle={t('quality.insightsSubtitle')} />
      <Filters onReset={reset}>
        <Field label={t('quality.period')}>
          <Select value={String(days)} onChange={(e) => set({ days: e.target.value === '30' ? undefined : e.target.value })}>
            {PERIODS.map((d) => (
              <option key={d} value={d}>
                {t('quality.lastDays', { count: d })}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('quality.organization')} className="w-64">
          <OrgSelect value={organizationId} onChange={(id) => set({ organization_id: id })} />
        </Field>
      </Filters>
      {error && <Alert>{error}</Alert>}
      {loading && !data && <p className="text-faint">{t('common.loading')}</p>}
      {data && (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            <Link to={listLink()}>
              <Stat label={t('quality.conversations')} value={data.conversations} hint={t('quality.turnsHint', { count: data.turns })} />
            </Link>
            <Link to={listLink({ flagged: 'true' })}>
              <Stat label={t('quality.flagged')} value={pct(data.conversations ? data.flagged / data.conversations : null)} hint={t('quality.flaggedHint', { count: data.flagged })}
                    tone={data.conversations && data.flagged / data.conversations > 0.25 ? 'warn' : undefined} />
            </Link>
            <Stat label={t('quality.satisfaction')} value={pct(data.satisfaction)} hint={t('quality.thumbsHint', { up: data.thumbs_up, down: data.thumbs_down })} />
            <Stat label={t('quality.responseRate')} value={pct(data.response_rate)} hint={t('quality.responseHint', { answered: data.card_answered, dismissed: data.card_dismissed })} />
            <Link to={listLink({ outcome: 'failure' })}>
              <Stat label={t('quality.successRate')} value={pct(data.success_rate)} hint={t('quality.reviewedHint', { count: data.reviewed })} />
            </Link>
            {/* Open now, whatever the window: the issues page lists them. */}
            <Link to="/issues" data-testid="issues-open">
              <Stat label={t('issues.openStat')} value={data.issues_open ?? 0}
                    hint={t('issues.openStatHint', { reports: data.reports ?? 0, opened: data.issues_opened ?? 0, closed: data.issues_closed ?? 0 })}
                    tone={data.issues_open ? 'bad' : undefined} />
            </Link>
            <Stat label={t('quality.reviewSpend')} value={usd(data.review_spend_usd, i18n.language)} hint={t('quality.reviewSpendHint')} />
            <Stat label={t('quality.sandboxSpend')} value={usd(data.sandbox_spend_usd ?? 0, i18n.language)} hint={t('quality.reviewSpendHint')} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <BarChart title={t('quality.conversationsPerDay')} buckets={data.per_day.map((d) => ({ period: d.day, value: d.conversations }))}
                      format={(v) => String(Math.round(v))} periodLabel={dayLabel} />
            <BarChart title={t('quality.flaggedPerDay')} buckets={data.per_day.map((d) => ({ period: d.day, value: d.flagged }))}
                      format={(v) => String(Math.round(v))} periodLabel={dayLabel} color="#0B5A2F" />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card title={t('quality.failureReasons')}>
              <RankList items={data.failure_reasons} label={reason} empty={t('quality.noReviews')} />
            </Card>
            <Card title={t('quality.misuse')}>
              <RankList items={data.misuse} label={misuseKind} empty={t('quality.noReviews')} />
            </Card>
            <Card title={t('quality.negativeTags')}>
              <RankList items={data.negative_tags} label={tag} empty={t('quality.noRatings')} onPick={() => navigate(listLink({ rating: 'down' }))} />
            </Card>
            <Card title={t('quality.signals')}>
              <RankList items={data.signals} label={signal} empty={t('common.empty')} onPick={() => navigate(listLink({ flagged: 'true' }))} />
            </Card>
            <Card title={t('quality.outcomes')}>
              <RankList items={data.outcomes} label={outcome} empty={t('quality.noReviews')} onPick={(k) => navigate(listLink({ outcome: k }))} />
            </Card>
            <Card title={t('quality.categories')}>
              <RankList items={data.categories} label={category} empty={t('quality.noReviews')} onPick={(k) => navigate(listLink({ category: k }))} />
            </Card>
            <Card title={t('quality.productInsights')}>
              {data.insights.length ? (
                <ul className="flex list-disc flex-col gap-1.5 pl-4 text-[12.5px] text-ink">
                  {data.insights.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-[12.5px] text-faint">{t('quality.noReviews')}</p>
              )}
            </Card>
          </div>

          <section>
            <h2 className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-faint">{t('quality.perOrganization')}</h2>
            <Table head={[t('quality.organization'), t('quality.conversations'), t('quality.flagged'), t('quality.thumbs'), t('quality.reviewed'), t('quality.failures'), t('quality.spend')]}
                   empty={t('common.empty')}>
              {data.per_organization.map((o) => (
                <Row key={o.organization_id ?? 'none'} onClick={o.organization_id ? () => navigate(listLink({ organization_id: String(o.organization_id) })) : undefined}>
                  <Cell>{o.name}</Cell>
                  <Cell mono>{o.conversations}</Cell>
                  <Cell mono>{o.flagged}</Cell>
                  <Cell mono>{o.thumbs_up} / {o.thumbs_down}</Cell>
                  <Cell mono>{o.reviewed}</Cell>
                  <Cell mono className={o.failures ? 'text-danger' : ''}>{o.failures}</Cell>
                  <Cell mono>{usd(o.spend_usd, i18n.language)}</Cell>
                </Row>
              ))}
            </Table>
          </section>
        </div>
      )}
    </div>
  );
}
